// client/src/contexts/AuthContext.js
//
// SESSION MODEL — 30-day absolute cap + deferred-logout-on-idle.
//
// IMPORTANT CONTEXT (why this version is back): this is the full design with
// the inactivity timer. It depends on the server exposing POST /auth/refresh
// (added in server/routes/auth.js). When that route was MISSING, every refresh
// 404'd, a failed refresh armed the cap-reached flag, and returning to the tab
// after a few minutes away logged you out. With the refresh route in place, the
// refresh-on-return now SUCCEEDS, so tabbing away to another site and coming
// back is a non-event — the token is simply renewed and you stay logged in.
//
// If this version still misbehaves after the server route is installed, swap in
// AuthContext-SIMPLE-no-inactivity.js, which drops all idle logic and lets the
// 30-day token be the sole logout trigger.
import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api.js';
import authService from '../services/authService.js';

export const AuthContext = createContext(null);

// ─── Session timing ───────────────────────────────────────
// The 30-day absolute cap is the ONLY hard logout trigger. It is enforced
// server-side: once a session's original login (sessionStart claim) is older
// than the cap, /auth/refresh refuses to renew and returns a cap failure.
//
// Idle time is NOT a logout reason on its own. Its only job is to find a
// polite moment to enforce an already-reached cap: when the cap trips while
// the user is actively working, we DON'T cut them off mid-task — we wait until
// they pause (IDLE_PAUSE_MS of no interaction) and log out then. An always-
// active user simply keeps working on their current token until it naturally
// expires (a small, intentional grace past the 30-day mark).
//
// REFRESH_INTERVAL_MS — while active, renew the token at least this often so
//                       an active session never expires mid-task before day 30.
// IDLE_PAUSE_MS       — once the cap is reached, how long a pause must last
//                       before we perform the deferred logout.
// TICK_MS             — how often the session loop checks the clocks.
const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const IDLE_PAUSE_MS = 10 * 60 * 1000;       // 10 minutes (deferred-logout pause)
const TICK_MS = 60 * 1000;                  // 1 minute

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Boolean drives the session-loop effect so it only re-runs on a true
  // login/logout transition — NOT every time the user object is replaced by
  // a refresh.
  const isAuthed = !!user;

  // Live mirrors for use inside timers / event handlers without stale closures.
  const userRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const lastRefreshRef = useRef(Date.now());
  // Set true once the server signals the 30-day cap has been reached. From
  // then on we stop refreshing and wait for an idle pause to log out.
  const capReachedRef = useRef(false);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Clear auth state + token without navigating. Used for passive/bootstrap
  // failures where there's no stale logged-in UI to tear down.
  const clearSession = useCallback(() => {
    api.clearToken();
    setUser(null);
  }, []);

  // Full reset: clear state + token, then hard-navigate. The page reload
  // guarantees no stale "looks logged in" UI can survive a logout.
  const forceReset = useCallback((path) => {
    api.clearToken();
    setUser(null);
    window.location.assign(path);
  }, []);

  // Load the current user from the token. Seeds the activity/refresh clocks
  // on success. Tolerates server hiccups (keeps state on non-401 errors).
  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const data = await authService.getMe();
      setUser(data.user);
      // Fresh authenticated load → start both clocks now, clear any cap flag.
      lastActivityRef.current = Date.now();
      lastRefreshRef.current = Date.now();
      capReachedRef.current = false;
    } catch (err) {
      // 401 → token is dead (api.js already tried to refresh it and failed).
      // Clear and render logged-out. Keep state on 500/network blips so a
      // transient server error doesn't flicker the user to logged-out.
      if (err.status === 401) {
        clearSession();
      }
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Register the terminal auth-failure handler that api.js calls when a 401
  // could not be refreshed (e.g. the cap was reached and a request then fired).
  useEffect(() => {
    api.setAuthFailureHandler(() => {
      if (userRef.current) {
        // We were logged in and the session ended (cap reached) → full reset
        // so the app can't keep showing a stale logged-in shell. To /login so
        // the user can sign back in.
        forceReset('/login');
      } else {
        // No active session in state (bootstrap with a stale token, or a
        // logged-out visitor on a public page) → just clear, don't redirect.
        clearSession();
      }
    });
  }, [forceReset, clearSession]);

  // The session loop. Reads refs so it doesn't need to tear down on every
  // user-object change.
  //
  //   • Not capped yet → keep the active session alive by refreshing on
  //     interval. A refresh that fails with the cap signal arms capReached
  //     instead of logging out.
  //   • Capped → do NOT refresh. Wait for a pause (IDLE_PAUSE_MS) and then
  //     perform the deferred logout. If the user never pauses, they keep
  //     working until the token naturally expires (api.js 401 backstop then
  //     ends it via the handler above).
  const checkSession = useCallback(async () => {
    if (!userRef.current) return;

    const now = Date.now();

    if (capReachedRef.current) {
      // Cap already reached — enforce it the moment the user pauses.
      if (now - lastActivityRef.current >= IDLE_PAUSE_MS) {
        forceReset('/login');
      }
      return; // never refresh once capped
    }

    // Not capped: renew the token ahead of expiry while active.
    if (now - lastRefreshRef.current >= REFRESH_INTERVAL_MS) {
      lastRefreshRef.current = now; // stamp first so a slow refresh isn't re-fired
      const refreshed = await authService.refresh();
      if (refreshed?.token) {
        if (refreshed.user) setUser(refreshed.user);
      } else {
        // Refresh failed → the 30-day cap was reached (or the session is
        // otherwise over). Don't cut an active user off — arm the deferred
        // logout and let the pause check above handle it.
        capReachedRef.current = true;
        // If they're already idle past the pause window, log out now.
        if (now - lastActivityRef.current >= IDLE_PAUSE_MS) {
          forceReset('/login');
        }
      }
    }
  }, [forceReset]);

  // Attach activity listeners + the tick loop only while logged in.
  useEffect(() => {
    if (!isAuthed) return;

    // Seed the clocks at the moment of login (this effect runs on the
    // logged-out → logged-in transition, not on every refresh).
    lastActivityRef.current = Date.now();
    lastRefreshRef.current = Date.now();
    capReachedRef.current = false;

    const markActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const onVisibility = () => {
      // Returning to the tab is a checkpoint: if we're capped and they were
      // away past the pause window, log out now; otherwise top up the token.
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };

    // Real interaction signals. mousemove is intentionally excluded as noise;
    // these cover genuine engagement without firing constantly.
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);

    const interval = setInterval(checkSession, TICK_MS);

    return () => {
      events.forEach((e) => window.removeEventListener(e, markActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [isAuthed, checkSession]);

  // ─── Public auth actions ────────────────────────────────

  const login = async (email, password, rememberMe = false) => {
    const data = await authService.login(email, password, rememberMe);
    localStorage.setItem('token', data.token);
    await loadUser();
    return data;
  };

  // Manual logout (e.g. navbar button). Full reset to home so nothing stale
  // lingers.
  const logout = () => {
    forceReset('/');
  };

  const loginWithToken = async (token) => {
    localStorage.setItem('token', token);
    await loadUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, loginWithToken, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;